/** Standalone cart interaction controller. Classic script, no module dependencies.
 * Captures product-form submissions and cart removal links before other handlers.
 * Server-rendered cart markup remains the source of truth; the dialog is preserved.
 */
class SCartInteractions extends HTMLElement {
  #abort;
  #busy = false;
  connectedCallback() {
    this.#abort = new AbortController();
    const options = { capture: true, signal: this.#abort.signal };
    window.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('s-product-form')) return;
      if (!form.elements.namedItem('id')) return;
      if (event.submitter && !event.submitter.matches('[name="add"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const url = new URL(this.dataset.addUrl, location.origin);
      this.#run(() => fetch(url, {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' },
        body: new FormData(form),
      }), form.closest('s-product-form'), true);
    }, options);
    window.addEventListener('click', (event) => {
      const link = event.target.closest?.('a[data-cart-remove-link]');
      if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#run(() => fetch(link.href, { credentials: 'same-origin', cache: 'no-store' }), link.closest('s-cart'), false);
    }, options);
  }
  disconnectedCallback() { this.#abort?.abort(); }
  async #run(request, owner, openDrawer) {
    if (this.#busy) return;
    this.#busy = true;
    owner?.setAttribute('aria-busy', 'true');
    try {
      const response = await request();
      if (!response.ok) {
        const text = await response.text();
        let message = this.dataset.error;
        try { const error = JSON.parse(text); message = error.description || error.message || message; } catch {}
        throw new Error(message);
      }
      await response.text();
      // Fetch the same preview URL, preserving theme selection and session cookies.
      const page = await fetch(location.href, { credentials: 'same-origin', cache: 'no-store' });
      if (!page.ok) throw new Error(this.dataset.error);
      const fresh = new DOMParser().parseFromString(await page.text(), 'text/html');
      const ids = ['cart-drawer-items', 'cart-drawer-footer-content', 'header-cart-trigger', 'cart-drawer-title', 'cart'];
      const replacements = ids.map((id) => ({ current: document.getElementById(id), next: fresh.getElementById(id) }));
      if (!fresh.getElementById('cart-drawer-items')) throw new Error(this.dataset.error);
      for (const { current, next } of replacements) {
        if (current && next) current.replaceChildren(...[...next.childNodes].map((node) => document.importNode(node, true)));
        if (current && next && next.hasAttribute('aria-label')) current.setAttribute('aria-label', next.getAttribute('aria-label'));
      }
      const dialog = document.getElementById('cart-drawer');
      if (openDrawer) {
        for (const panel of document.querySelectorAll('.quick-add-panel[popover]')) {
          if (panel.matches(':popover-open')) panel.hidePopover();
        }
        if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
      }
    } catch (error) {
      const inline = owner?.querySelector('[data-add-to-cart-error]');
      if (inline) { inline.textContent = error.message || this.dataset.error; inline.hidden = false; }
      document.dispatchEvent(new CustomEvent('s-notification:add', { detail: { message: error.message || this.dataset.error, variant: 'error' } }));
    } finally {
      this.#busy = false;
      owner?.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('s-cart-interactions')) customElements.define('s-cart-interactions', SCartInteractions);
